/**
 * Splits: consulta de la config split_to/split_rate por collector + proyección
 * estimada de montos. Es API de webadmin2 → JWT post-MFA (`webadmin2.jwt`), igual
 * que collectorStats.ts. Los endpoints los sirve la lambda `billing` (sin ruta
 * propia: caen en su catch-all ANY /{proxy+}).
 *
 * OJO: el split NO se aplica en el core (config definida pero no consumida). La
 * proyección es una ESTIMACIÓN = cobros × split_rate, no plata liquidada.
 *
 * La ESCRITURA (crear/editar/borrar el split) NO va por acá: usa setConfigValue /
 * removeConfigKey de collectors.ts (adminApi con el corejwt).
 */
const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ?? "http://localhost:3000";

export interface SplitRow {
  collector:  string;
  split_to:   string | null;   // partner: IPN | Pedix | OrdenApp
  split_rate: number | null;   // fracción (0.05 = 5%)
  complete:   boolean;         // tiene partner Y rate
}

export interface SplitProjectionRow {
  collector: string;
  partner:   string;
  rate:      number;
  cobros:    number;
  projected: number;
}

export interface SplitPartnerTotal {
  partner:    string;
  cobros:     number;
  projected:  number;
  collectors: number;
}

/** Una fila por collector × mes × acquirer (base del reporte CSV por socio, CP-334). */
export interface SplitProjectionDetailRow {
  collector:       string;
  partner:         string;
  rate:            number;
  month:           string;   // yyyyMM
  acquirer:        string;   // AR#BIND | AR#CMFP | MX#STP
  currency:        string;   // ARS | MXN
  count:           number;
  amount:          number;   // bruto (incluye rechazados, igual que `cobros` en pantalla)
  count_rejected:  number;
  amount_rejected: number;
  projected:       number;   // round(amount × rate, 2)
  partial:         boolean;  // el mes estaba en curso cuando se calculó el rollup
}

export interface SplitProjection {
  months:       string[];
  rows:         SplitProjectionRow[];
  by_partner:   SplitPartnerTotal[];
  /** Opcional para tolerar un backend viejo (sin deploy del detalle): el reporte sale igual, sin filas mensuales. */
  detail?:      SplitProjectionDetailRow[];
  estimate:     boolean;
  generated_at: string;
}

export const SPLIT_PARTNERS = ["IPN", "Pedix", "OrdenApp"] as const;

function authHeader(): Record<string, string> {
  const jwt = localStorage.getItem("webadmin2.jwt");
  return jwt ? { Authorization: `Bearer ${jwt}` } : {};
}

async function authFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const res = await fetch(url, init);
  if (res.status === 401) {
    localStorage.removeItem("webadmin2.jwt");
    localStorage.removeItem("webadmin2.email");
    if (typeof window !== "undefined") window.location.replace("/login");
  }
  return res;
}

// ----- ABM de partners (catálogo split_to) — lambda `collectors` -----------
export async function getPartners(): Promise<string[]> {
  const res = await authFetch(`${API_BASE}/collectors/partners`, { headers: authHeader() });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error ?? `getPartners: ${res.status}`);
  return (data.partners ?? []) as string[];
}

export async function addPartner(name: string): Promise<string[]> {
  const res = await authFetch(`${API_BASE}/collectors/partners`, {
    method: "POST", headers: { "Content-Type": "application/json", ...authHeader() },
    body: JSON.stringify({ name }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error ?? `addPartner: ${res.status}`);
  return (data.partners ?? []) as string[];
}

export async function removePartner(name: string): Promise<string[]> {
  const res = await authFetch(`${API_BASE}/collectors/partners/${encodeURIComponent(name)}`, {
    method: "DELETE", headers: authHeader(),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const e = new Error(data.error ?? `removePartner: ${res.status}`) as Error & { collectors?: string[] };
    e.collectors = data.collectors;
    throw e;
  }
  return (data.partners ?? []) as string[];
}

export async function getSplits(): Promise<{ items: SplitRow[]; generated_at: string }> {
  const res = await authFetch(`${API_BASE}/splits`, { headers: authHeader() });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error ?? `getSplits: ${res.status}`);
  return data;
}

/** from/to en formato YYYYMM (opcionales; default = mes en curso). */
export async function getSplitProjection(from?: string, to?: string): Promise<SplitProjection> {
  const qs = new URLSearchParams();
  if (from) qs.set("from", from);
  if (to) qs.set("to", to);
  const res = await authFetch(`${API_BASE}/splits/projection?${qs}`, { headers: authHeader() });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error ?? `getSplitProjection: ${res.status}`);
  return data as SplitProjection;
}
